import { Router } from "express";

import { adminController } from "../controllers/admin-controller";
import { attendanceController } from "../controllers/attendance-controller";
import { chatController } from "../controllers/chat-controller";
import {
  authenticate,
  createRateLimit,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import { validateTeacherFilter } from "../validation/teacher-validation";

const router = Router();

// Apply authentication and admin role requirement to all routes
router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["admin"]));

/**
 * ADMIN TEACHER MANAGEMENT ROUTES
 */

/**
 * @route   GET /api/v1/admin/teachers
 * @desc    Get all teachers across all schools with advanced filtering and pagination
 * @access  Private (Admin)
 * @query   approvalStatus, status, subject, search, page, limit, sortBy, sortOrder, dateFrom, dateTo, schoolId, hasDocuments, isEmailVerified
 */
router.get("/teachers", validateTeacherFilter, (req, res) =>
  adminController.getAllTeachers(req, res),
);

/**
 * @route   GET /api/v1/admin/teachers/statistics
 * @desc    Get teacher statistics across all schools
 * @access  Private (Admin)
 */
router.get("/teachers/statistics", (req, res) =>
  adminController.getTeacherStatistics(req, res),
);

/**
 * @route   GET /api/v1/admin/teachers/:id
 * @desc    Get teacher by ID with full details (admin view)
 * @access  Private (Admin)
 */
router.get("/teachers/:id", (req, res) =>
  adminController.getTeacherById(req, res),
);

/**
 * ADMIN STUDENT MANAGEMENT ROUTES
 */
/**
 * @route   GET /api/v1/admin/students
 * @desc    Get all students across all schools with filtering and pagination
 * @access  Private (Admin)
 */
router.get("/students", (req, res) => adminController.getAllStudents(req, res));

/**
 * @route   PUT /api/v1/admin/students/:id/approve
 * @desc    Approve a student (admin)
 * @access  Private (Admin)
 */
router.put("/students/:id/approve", (req, res) =>
  adminController.approveStudent(req, res),
);

/**
 * @route   PUT /api/v1/admin/students/:id/reject
 * @desc    Reject a student (admin) with reason
 * @access  Private (Admin)
 */
router.put("/students/:id/reject", (req, res) =>
  adminController.rejectStudent(req, res),
);

/**
 * ADMIN SCHOOL MANAGEMENT ROUTES
 */

/**
 * @route   GET /api/v1/admin/schools
 * @desc    Get all schools with filtering and pagination
 * @access  Private (Admin/Super Admin)
 * @query   status, verificationStatus, isVerified, search, page, limit, sortBy, sortOrder
 */
router.get("/schools", (req, res) => adminController.getAllSchools(req, res));

/**
 * @route   GET /api/v1/admin/schools/:id
 * @desc    Get school by ID with full details (admin view)
 * @access  Private (Admin/Super Admin)
 */
router.get("/schools/:id", (req, res) =>
  adminController.getSchoolById(req, res),
);

/**
 * @route   PUT /api/v1/admin/schools/:id/approve
 * @desc    Approve school verification
 * @access  Private (Admin/Super Admin)
 */
router.put("/schools/:id/approve", (req, res) =>
  adminController.approveSchool(req, res),
);

/**
 * @route   PUT /api/v1/admin/schools/:id/reject
 * @desc    Reject school verification with reason
 * @access  Private (Admin/Super Admin)
 */
router.put("/schools/:id/reject", (req, res) =>
  adminController.rejectSchool(req, res),
);

/**
 * ADMIN ATTENDANCE ROUTES
 */

/**
 * @route   GET /api/v1/admin/schools/:schoolId/attendance
 * @desc    Get overall school attendance (Admin view)
 * @access  Private (Admin)
 * @query   startDate, endDate, classId, page, limit
 */
router.get("/schools/:schoolId/attendance", (req, res) =>
  attendanceController.getAdminSchoolAttendance(req, res),
);

/**
 * ADMIN CHAT ROUTES
 */

const chatRateLimit = createRateLimit.general();

/**
 * @route   GET /api/v1/admin/chats/teachers
 * @desc    Get filtered teachers for admin panel chat creation (by name, email)
 * @access  Private (Admin)
 */
router.get("/chats/teachers", chatRateLimit, (req, res) =>
  chatController.getAdminTeachersForChat(req, res),
);

/**
 * @route   GET /api/v1/admin/chats/students
 * @desc    Get filtered students for admin panel chat creation (by name, email)
 * @access  Private (Admin)
 */
router.get("/chats/students", chatRateLimit, (req, res) =>
  chatController.getAdminStudentsForChat(req, res),
);

/**
 * @route   GET /api/v1/admin/chats/schools
 * @desc    Get filtered schools for admin panel chat creation (by name, email)
 * @access  Private (Admin)
 */
router.get("/chats/schools", chatRateLimit, (req, res) =>
  chatController.getAdminSchoolsForChat(req, res),
);

/**
 * @route   POST /api/v1/admin/chats
 * @desc    Create chat from admin panel with teacher, student, or school
 * @access  Private (Admin)
 */
router.post("/chats", chatRateLimit, (req, res) =>
  chatController.createAdminChat(req, res),
);

export default router;
